/**
 * Pure helpers for deciding which attached files we accept. Kept separate from
 * the `useFileAttachment` hook so they can be unit-tested without the React /
 * toast dependency chain, and shared identically by the picker, paste, and drop
 * paths.
 */

/**
 * Check if a file's type matches the supported accept list.
 * Handles wildcards (image/*), MIME types (image/png), and extensions (.pdf).
 */
export function isFileTypeSupported(file: File, acceptTypes: string): boolean {
  const accepts = acceptTypes.split(",").map((t) => t.trim());

  for (const accept of accepts) {
    // Handle wildcards like "image/*"
    if (accept.endsWith("/*")) {
      const category = accept.slice(0, -2);
      if (file.type.startsWith(`${category}/`)) return true;
    } // Handle MIME types
    else if (accept.includes("/")) {
      if (file.type === accept) return true;
    } // Handle extensions like ".pdf"
    else if (accept.startsWith(".")) {
      if (file.name.toLowerCase().endsWith(accept.toLowerCase())) return true;
    }
  }
  return false;
}

/**
 * Split files into those we accept and those we don't, so callers can attach the
 * supported ones and tell the user (via toast) about the rest. Centralizing this
 * means the picker, paste, and drop paths give identical, visible feedback for
 * an unsupported file instead of silently dropping it (or 400'ing at send).
 */
export function partitionBySupport(
  files: File[],
  acceptTypes: string,
): { supported: File[]; rejected: File[] } {
  const supported: File[] = [];
  const rejected: File[] = [];
  for (const file of files) {
    (isFileTypeSupported(file, acceptTypes) ? supported : rejected).push(file);
  }
  return { supported, rejected };
}
